// DEC-016: fallo parcial del wizard de crear aplicación = BORRADOR EDITABLE, no compensación
// destructiva. Si tras `POST /applications` fallan los permisos CUSTOM o el `PUT` de reconciliación de
// seedRoles, la app NO se borra: queda creada como plantilla parcial válida y el operador la completa
// desde el detalle (Task E). A diferencia de la saga de onboarding (que sí compensa pools/users
// huérfanos = infra tóxica), una plantilla parcial no es tóxica. Ver
// ../platform-rbac-business/docs/specs/2026-06-25-armador-aplicaciones-3a-design.md (Task D, decisión).
import type {
    ApplicationPermissionDraft,
    CreateApplicationRequestLocal,
    SeedRoleWire,
    UpsertApplicationPermissionRequestLocal,
} from '../../../api/types/ApplicationLocal';

/** Fases de la orquestación multi-call del submit del wizard. */
export type SubmitPhase =
    | 'idle'
    | 'creating-app'
    | 'creating-permissions'
    | 'reconciling-roles'
    | 'done'
    | 'error';

/**
 * Permisos CUSTOM del buffer en memoria (los SYSTEM vienen de la plantilla sembrada por el backend al
 * crear; no se envían). El padre los manda uno por uno a `POST /applications/{id}/permissions`.
 */
export function customPermissions(
    catalog: ApplicationPermissionDraft[],
): ApplicationPermissionDraft[] {
    return catalog.filter((p) => p.kind === 'CUSTOM');
}

/**
 * Proyección de un permiso CUSTOM del buffer al body de `POST /applications/{id}/permissions`
 * (`UpsertApplicationPermissionRequest`). El `kind` no viaja: el endpoint solo crea CUSTOM.
 */
export function permissionToUpsertBody(
    perm: ApplicationPermissionDraft,
): UpsertApplicationPermissionRequestLocal {
    return {
        permissionKey: perm.permissionKey,
        displayName: perm.displayName,
        description: perm.description,
        scope: perm.scope,
        ...(perm.category ? { category: perm.category } : {}),
    };
}

/**
 * Keys de los permisos CUSTOM definidos en el buffer del catálogo. Se usa para detectar qué seedRoles
 * referencian CUSTOM (y por ende deben diferirse hasta que el catálogo CUSTOM exista en el backend).
 */
export function customPermissionKeys(catalog: ApplicationPermissionDraft[]): Set<string> {
    return new Set(customPermissions(catalog).map((p) => p.permissionKey));
}

/** True si el rol referencia al menos un permiso CUSTOM (del buffer). */
export function roleReferencesCustom(role: SeedRoleWire, customKeys: Set<string>): boolean {
    return role.permissions.some((k) => customKeys.has(k));
}

/** True si ALGÚN seedRole referencia permisos CUSTOM → hace falta el PUT de reconciliación (fase 3). */
export function needsRoleReconciliation(
    seedRoles: SeedRoleWire[],
    catalog: ApplicationPermissionDraft[],
): boolean {
    const customKeys = customPermissionKeys(catalog);
    return seedRoles.some((r) => roleReferencesCustom(r, customKeys));
}

/**
 * seedRoles "seguros" para el `POST /applications` inicial: con los permisos CUSTOM filtrados (solo
 * quedan refs a permisos SYSTEM, que YA existen en el catálogo sembrado). Los CUSTOM se reconcilian con
 * el `PUT` de fase 3, una vez que el catálogo CUSTOM existe en el backend. Si un rol queda sin permisos
 * tras el filtro, igual se envía (su displayName/scope son válidos; el PUT le repone los CUSTOM).
 *
 * Motivo: el backend valida los `permissions[]` de cada seedRole contra el catálogo de la app, y el
 * catálogo CUSTOM aún no existe en el `POST` (los endpoints de permisos son separados y requieren la
 * app ya creada). Mandar refs CUSTOM en el `POST` haría que el backend las rechace.
 */
export function seedRolesForInitialCreate(
    seedRoles: SeedRoleWire[],
    catalog: ApplicationPermissionDraft[],
): SeedRoleWire[] {
    const customKeys = customPermissionKeys(catalog);
    return seedRoles.map((r) => ({
        ...r,
        permissions: r.permissions.filter((k) => !customKeys.has(k)),
    }));
}

/** Body del `POST /applications` inicial (con seedRoles podados de refs CUSTOM). */
export interface BuildCreateBodyArgs {
    applicationId: string;
    displayName: string;
    levels: CreateApplicationRequestLocal['levels'];
    seedRoles: SeedRoleWire[];
    catalog: ApplicationPermissionDraft[];
    defaults: CreateApplicationRequestLocal['defaults'];
}

export function buildCreateBody(args: BuildCreateBodyArgs): CreateApplicationRequestLocal {
    return {
        applicationId: args.applicationId,
        displayName: args.displayName,
        levels: args.levels,
        seedRoles: seedRolesForInitialCreate(args.seedRoles, args.catalog),
        defaults: args.defaults,
    };
}
